/**
 * ListRow — one card row.
 *
 * Hover-revealed selection checkbox per Notion convention. The whole
 * row is keyboard-focusable; Enter / Space open the card detail.
 */
import type { Card } from '@chaos/types/kanban';
import * as React from 'react';

import { cn } from '../../../lib/utils';
import { type ListColumnDef, rowTemplateColumns, STATIC_ROW_HEIGHT } from './list-columns';

export interface ListRowProps {
  card: Card;
  columns: readonly ListColumnDef[];
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onOpen?: ((id: string) => void) | undefined;
}

export const ListRow = React.memo(function ListRow({
  card,
  columns,
  selected,
  onToggleSelect,
  onOpen,
}: ListRowProps) {
  const grid = React.useMemo(() => rowTemplateColumns(columns), [columns]);

  return (
    <div
      role="row"
      aria-selected={selected}
      tabIndex={0}
      onClick={() => onOpen?.(card.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.(card.id);
        }
      }}
      className={cn(
        'group grid items-center gap-3 border-b border-border px-3 text-foreground',
        'cursor-pointer outline-none transition-colors',
        'hover:bg-foreground/[0.03] focus-visible:bg-foreground/[0.04]',
        selected && 'bg-foreground/[0.05]',
      )}
      style={{ gridTemplateColumns: `28px ${grid}`, height: STATIC_ROW_HEIGHT }}
    >
      <div
        className={cn(
          'flex items-center justify-center',
          'opacity-0 group-hover:opacity-100 transition-opacity',
          selected && 'opacity-100',
        )}
      >
        <input
          type="checkbox"
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleSelect(card.id)}
          aria-label={`Select ${card.title}`}
          className="h-3.5 w-3.5 rounded border-border accent-foreground"
        />
      </div>
      {columns.map((c) => (
        <div key={c.id} className="min-w-0 truncate" role="cell">
          {c.cell(card)}
        </div>
      ))}
    </div>
  );
});
